import axios from 'axios';
import { captureException, addBreadcrumb } from '../config/sentry';
import { appendOrganizationHeader, getCurrentOrganizationId } from './organizationContext';

// ─── API Base URL Configuration ──────────────────────────────────────────────
// Local dev: Use relative /api (Vite proxy handles it, no CORS needed)
// Production: Use explicit origin from env var or Fly.io backend
const getApiBaseUrl = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  if (typeof window === 'undefined') return '/api';

  const isLocalhost =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';

  if (isLocalhost) return '/api';
  return 'https://api.gabhan.io/api';
};

const API_BASE_URL = getApiBaseUrl();

// ─── Request Correlation ─────────────────────────────────────────────────────
let _requestCounter = 0;
const generateRequestId = () => `req_${Date.now()}_${++_requestCounter}`;

// ─── GET Request Deduplication ───────────────────────────────────────────────
// Coalesces identical in-flight GET requests into a single network call
const _inflightGets = new Map();

const deduplicatedGet = (url, config = {}) => {
  const key = `${url}|${JSON.stringify(config.params || {})}`;
  const existing = _inflightGets.get(key);
  if (existing) return existing;

  const promise = api.get(url, config).finally(() => _inflightGets.delete(key));
  _inflightGets.set(key, promise);
  return promise;
};

// ─── Request Timing & Metrics ────────────────────────────────────────────────
const _apiMetrics = {
  totalRequests: 0,
  totalErrors: 0,
  totalDurationMs: 0,
  byEndpoint: {}, // { '/products': { count, totalMs, errors } }
  recentTimings: [], // last 100 request timings for p95
};

const MAX_TIMING_HISTORY = 100;

const recordTiming = (method, url, durationMs, success) => {
  _apiMetrics.totalRequests++;
  if (!success) _apiMetrics.totalErrors++;
  _apiMetrics.totalDurationMs += durationMs;

  // Normalize URL by stripping IDs (e.g. /products/abc123 → /products/:id)
  const normalizedUrl = (url || '').replace(/\/[a-f0-9]{24}/gi, '/:id');
  const endpointKey = `${method?.toUpperCase()} ${normalizedUrl}`;

  if (!_apiMetrics.byEndpoint[endpointKey]) {
    _apiMetrics.byEndpoint[endpointKey] = { count: 0, totalMs: 0, errors: 0, maxMs: 0 };
  }
  const ep = _apiMetrics.byEndpoint[endpointKey];
  ep.count++;
  ep.totalMs += durationMs;
  if (!success) ep.errors++;
  if (durationMs > ep.maxMs) ep.maxMs = durationMs;

  _apiMetrics.recentTimings.push(durationMs);
  if (_apiMetrics.recentTimings.length > MAX_TIMING_HISTORY) {
    _apiMetrics.recentTimings.shift();
  }
};

/**
 * Get API performance metrics snapshot
 * @returns {{ totalRequests, totalErrors, avgMs, p95Ms, errorRate, byEndpoint }}
 */
export const getApiMetrics = () => {
  const sorted = [..._apiMetrics.recentTimings].sort((a, b) => a - b);
  const p95Index = Math.floor(sorted.length * 0.95);
  return {
    totalRequests: _apiMetrics.totalRequests,
    totalErrors: _apiMetrics.totalErrors,
    errorRate: _apiMetrics.totalRequests
      ? (_apiMetrics.totalErrors / _apiMetrics.totalRequests * 100).toFixed(1) + '%'
      : '0%',
    avgMs: _apiMetrics.totalRequests
      ? Math.round(_apiMetrics.totalDurationMs / _apiMetrics.totalRequests)
      : 0,
    p95Ms: sorted[p95Index] ? Math.round(sorted[p95Index]) : 0,
    byEndpoint: { ..._apiMetrics.byEndpoint },
  };
};

// ─── Offline Mutation Queue ──────────────────────────────────────────────────
// Queues POST/PUT/PATCH/DELETE when offline; replays on reconnect
const OFFLINE_QUEUE_KEY = 'gabhan_offline_queue';

const _offlineQueue = {
  _queue: [],

  init() {
    try {
      const stored = localStorage.getItem(OFFLINE_QUEUE_KEY);
      if (stored) this._queue = JSON.parse(stored);
    } catch { /* empty */ }

    window.addEventListener('online', () => this.flush());
  },

  enqueue(config) {
    this._queue.push({
      method: config.method,
      url: config.url,
      data: config.data,
      timestamp: Date.now(),
    });
    this._persist();
  },

  _persist() {
    try { localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(this._queue)); } catch { /* full */ }
  },

  async flush() {
    if (this._queue.length === 0) return;
    const pending = [...this._queue];
    this._queue = [];
    this._persist();

    let replayed = 0;
    for (const req of pending) {
      try {
        await api.request({ method: req.method, url: req.url, data: req.data });
        replayed++;
      } catch {
        // Re-enqueue on failure
        this._queue.push(req);
      }
    }
    this._persist();
    if (replayed > 0) {
      window.dispatchEvent(new CustomEvent('offline-queue-flushed', { detail: { replayed, remaining: this._queue.length } }));
    }
  },

  get length() { return this._queue.length; },
  get items() { return [...this._queue]; },
  clear() { this._queue = []; this._persist(); },
};

_offlineQueue.init();

/** Get offline queue state */
export const getOfflineQueue = () => ({
  length: _offlineQueue.length,
  items: _offlineQueue.items,
});

/** Force-flush the offline queue */
export const flushOfflineQueue = () => _offlineQueue.flush();

// ─── Cancel Token Registry ──────────────────────────────────────────────────
// Enables component-scoped request cancellation on unmount
const _cancelControllers = new Map();

/**
 * Create a cancellation scope (use in useEffect cleanup)
 * @param {string} scopeId — unique ID (e.g. component name)
 * @returns {{ signal: AbortSignal, cancel: () => void }}
 */
export const createCancelScope = (scopeId) => {
  // Cancel previous scope if still active
  _cancelControllers.get(scopeId)?.abort();
  const controller = new AbortController();
  _cancelControllers.set(scopeId, controller);
  return {
    signal: controller.signal,
    cancel: () => {
      controller.abort();
      _cancelControllers.delete(scopeId);
    },
  };
};

// ─── Token Refresh Lock ──────────────────────────────────────────────────────
// Prevents multiple concurrent 401 responses from triggering parallel refresh
// attempts. Uses a mutex pattern: first request to hit 401 initiates the refresh,
// subsequent 401s queue and await the same refresh promise.
let _isRefreshing = false;
let _refreshPromise = null;
const _failedQueue = []; // { resolve, reject }[]

// ── Token Refresh Instrumentation ─────────────────────────────────────────
const _refreshMetrics = {
  totalAttempts: 0,
  totalSuccesses: 0,
  totalFailures: 0,
  totalQueued: 0,
  peakQueueDepth: 0,
  lastAttemptAt: null,
  lastOutcome: null,      // 'success' | 'failure'
  lastDurationMs: null,
  history: [],            // last 20 refresh events
};

const _REFRESH_HISTORY_MAX = 20;

const _recordRefreshEvent = (event) => {
  _refreshMetrics.history.push(event);
  if (_refreshMetrics.history.length > _REFRESH_HISTORY_MAX) {
    _refreshMetrics.history = _refreshMetrics.history.slice(-_REFRESH_HISTORY_MAX);
  }
};

/** Get token refresh diagnostics for monitoring / dev tools */
export const getRefreshDiagnostics = () => ({ ..._refreshMetrics, history: [..._refreshMetrics.history] });

const _processRefreshQueue = (error, token = null) => {
  const queueDepth = _failedQueue.length;
  _failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  _failedQueue.length = 0;
  return queueDepth;
};

const _attemptTokenRefresh = async () => {
  // Guard: if another call already started refresh, piggyback on it
  if (_isRefreshing && _refreshPromise) {
    _refreshMetrics.totalQueued++;
    _refreshMetrics.peakQueueDepth = Math.max(_refreshMetrics.peakQueueDepth, _failedQueue.length + 1);
    return _refreshPromise;
  }

  _isRefreshing = true;
  _refreshMetrics.totalAttempts++;
  _refreshMetrics.lastAttemptAt = new Date().toISOString();
  const startTime = performance.now();

  _refreshPromise = (async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        { refreshToken: localStorage.getItem('refresh_token') },
        { timeout: 10_000 } // 10s hard timeout
      );

      const { accessToken, refreshToken: newRefreshToken } = response.data;
      if (!accessToken) throw new Error('No access token in refresh response');

      localStorage.setItem('access_token', accessToken);
      if (newRefreshToken) {
        localStorage.setItem('refresh_token', newRefreshToken);
      }

      const queueDepth = _processRefreshQueue(null, accessToken);
      const durationMs = Math.round(performance.now() - startTime);

      _refreshMetrics.totalSuccesses++;
      _refreshMetrics.lastOutcome = 'success';
      _refreshMetrics.lastDurationMs = durationMs;
      const event = {
        event: 'token_refresh',
        outcome: 'success',
        durationMs,
        queueDepth,
        timestamp: new Date().toISOString(),
      };
      _recordRefreshEvent(event);
      if (typeof console !== 'undefined') console.debug('[Auth Refresh]', event);

      return accessToken;
    } catch (err) {
      const queueDepth = _processRefreshQueue(err);
      const durationMs = Math.round(performance.now() - startTime);

      _refreshMetrics.totalFailures++;
      _refreshMetrics.lastOutcome = 'failure';
      _refreshMetrics.lastDurationMs = durationMs;
      const event = {
        event: 'token_refresh',
        outcome: 'failure',
        durationMs,
        queueDepth,
        error: err.message,
        timestamp: new Date().toISOString(),
      };
      _recordRefreshEvent(event);
      console.warn('[Auth Refresh]', event);

      // Refresh failed — wipe tokens and redirect
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');

      window.dispatchEvent(new CustomEvent('session-expired', {
        detail: { message: 'Your session has expired. Please log in again.', redirectTo: '/login' },
      }));

      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login?expired=true';
      }
      throw err;
    } finally {
      _isRefreshing = false;
      _refreshPromise = null;
    }
  })();

  return _refreshPromise;
};

// ─── Axios Instance ──────────────────────────────────────────────────────────
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000, // 30s default timeout
});

// ─── Request Interceptor ─────────────────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const withOrg = appendOrganizationHeader(config);

    // Attach auth token
    const token = localStorage.getItem('access_token');
    if (token) {
      withOrg.headers.Authorization = `Bearer ${token}`;
    } else {
      delete withOrg.headers.Authorization;
    }

    // Always derive the active org from storage, never stale component state.
    const organizationId = getCurrentOrganizationId();
    if (organizationId) {
      withOrg.headers['X-Organization-Id'] = organizationId;
    }

    // Attach correlation ID
    withOrg.headers['X-Request-ID'] = generateRequestId();

    // Record start time for response timing
    withOrg.metadata = { startTime: performance.now() };

    // Sentry breadcrumb
    addBreadcrumb({
      category: 'api',
      message: `${withOrg.method?.toUpperCase()} ${withOrg.url}`,
      level: 'info',
    });

    return withOrg;
  },
  (error) => Promise.reject(error)
);

// ─── Response Interceptor ────────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => {
    // Record timing
    const durationMs = response.config.metadata?.startTime
      ? performance.now() - response.config.metadata.startTime
      : 0;
    recordTiming(response.config.method, response.config.url, durationMs, true);

    // Parse rate limit headers
    const remaining = response.headers['x-ratelimit-remaining'];
    const limit = response.headers['x-ratelimit-limit'];
    const reset = response.headers['x-ratelimit-reset'];

    if (remaining && limit) {
      const remainingNum = parseInt(remaining, 10);
      const limitNum = parseInt(limit, 10);
      const percentage = (remainingNum / limitNum) * 100;

      // Emit warning when below 25% remaining
      if (percentage < 25 && percentage > 0) {
        const event = new CustomEvent('rate-limit-warning', {
          detail: {
            remaining: remainingNum,
            limit: limitNum,
            reset: reset ? parseInt(reset, 10) : null,
            percentage,
          }
        });
        window.dispatchEvent(event);
      }
    }

    return response;
  },
  async (error) => {
    // Record timing for failed requests
    const durationMs = error.config?.metadata?.startTime
      ? performance.now() - error.config.metadata.startTime
      : 0;
    recordTiming(error.config?.method, error.config?.url, durationMs, false);

    // ── Offline queue: enqueue mutations when offline ──
    if (!navigator.onLine && error.config && !['get', 'head', 'options'].includes(error.config.method)) {
      _offlineQueue.enqueue(error.config);
      return Promise.reject(Object.assign(error, { _queued: true }));
    }

    // Handle rate limiting (429 Too Many Requests)
    if (error.response?.status === 429) {
      const retryAfter = error.response.data?.retryAfter || error.response.headers['retry-after'];
      const errorMessage = error.response.data?.error || 'Too many requests. Please slow down.';

      // Add user-friendly notification
      const event = new CustomEvent('rate-limit-exceeded', {
        detail: {
          message: errorMessage,
          retryAfter: retryAfter,
          endpoint: error.config?.url
        }
      });
      window.dispatchEvent(event);

      // Capture in Sentry
      captureException(new Error('Rate limit exceeded'), {
        api_endpoint: error.config?.url,
        api_method: error.config?.method,
        retry_after: retryAfter,
        limit: error.response.data?.limit,
        remaining: error.response.data?.remaining
      });

      return Promise.reject(error);
    }

    // Handle plan limit exceeded (403 with specific code)
    if (error.response?.status === 403 && error.response?.data?.code === 'PLAN_LIMIT_EXCEEDED') {
      const limitError = error.response.data;

      // Dispatch custom event for UI notification
      const event = new CustomEvent('plan-limit-exceeded', {
        detail: {
          resource: limitError.resource,
          current: limitError.current,
          limit: limitError.limit,
          message: limitError.message,
          upgradeUrl: limitError.upgradeUrl || '/subscription',
        }
      });
      window.dispatchEvent(event);

      // Optionally show modal or redirect
      // For better UX, let components handle this via the event listener

      return Promise.reject(error);
    }

    // Capture API errors in Sentry (except for expected 401s, 403s, and 429s)
    if (error.response?.status !== 401 && error.response?.status !== 403 && error.response?.status !== 429) {
      captureException(error, {
        api_endpoint: error.config?.url,
        api_method: error.config?.method,
        status_code: error.response?.status,
        response_data: error.response?.data,
      });
    }

    // ── User-facing toast for 5xx and network errors ──
    // Components that handle their own errors can set `config._silentError = true` to suppress.
    if (!error.config?._silentError) {
      const status = error.response?.status;
      if (status >= 500) {
        // Server error — actionable toast
        import('../utils/toast.js').then(({ default: toast }) => {
          const serverMsg = error.response?.data?.error;
          toast.error(serverMsg || 'Server error — please try again in a moment.', {
            duration: 6000,
            deduplicate: true,
          });
        }).catch(() => {});
      } else if (!error.response && error.code !== 'ERR_CANCELED') {
        // Network error (no response at all, and not an intentional abort)
        import('../utils/toast.js').then(({ default: toast }) => {
          toast.error('Network error — check your connection and try again.', {
            duration: 6000,
            deduplicate: true,
          });
        }).catch(() => {});
      }
    }

    if (error.response?.status === 401) {
      const originalRequest = error.config;

      // Don't retry auth endpoints or already-retried requests
      if (
        originalRequest?._retry ||
        originalRequest?.url?.includes('/auth/refresh') ||
        originalRequest?.url?.includes('/auth/login')
      ) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login?expired=true';
        }
        return Promise.reject(error);
      }

      // Check if we have a refresh token to attempt silent refresh
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        window.dispatchEvent(new CustomEvent('session-expired', {
          detail: { message: 'Your session has expired. Please log in again.', redirectTo: '/login' },
        }));
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login?expired=true';
        }
        return Promise.reject(error);
      }

      // If already refreshing, queue this request to avoid race condition
      if (_isRefreshing) {
        return new Promise((resolve, reject) => {
          _failedQueue.push({ resolve, reject });
        }).then((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          originalRequest._retry = true;
          return api(originalRequest);
        });
      }

      // Attempt refresh — first caller wins, others queue
      originalRequest._retry = true;
      try {
        const newToken = await _attemptTokenRefresh();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Products API
export const productsAPI = {
  getAll: (params) => api.get('/products', { params }),
  search: (params) => api.get('/products/search', { params }), // Enterprise search endpoint
  getFacets: (params) => api.get('/products/search/facets', { params }), // Facets for filtering
  autocomplete: (params) => api.get('/analytics/search/autocomplete', { params }), // Autocomplete suggestions
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

// Orders API
export const ordersAPI = {
  getAll: (params) => api.get('/orders', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  delete: (id) => api.delete(`/orders/${id}`),
};

// Suppliers API
export const suppliersAPI = {
  getAll: (params) => api.get('/suppliers', { params }),
  getById: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
};

// Warehouses API
export const warehousesAPI = {
  getAll: (params) => api.get('/warehouses', { params }),
  getById: (id) => api.get(`/warehouses/${id}`),
  create: (data) => api.post('/warehouses', data),
  update: (id, data) => api.put(`/warehouses/${id}`, data),
  delete: (id) => api.delete(`/warehouses/${id}`),
  getDiscrepancySummary: (id) => api.get(`/warehouses/${id}/discrepancy-summary`),
};

// Purchase Orders API
export const purchaseOrdersAPI = {
  getAll: (params) => api.get('/purchase-orders', { params }),
  getById: (id) => api.get(`/purchase-orders/${id}`),
  create: (data) => api.post('/purchase-orders', data),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
  updateStatus: (id, status) => api.patch(`/purchase-orders/${id}/status`, { status }),
};

// Warehouse Transfers API
export const warehouseTransfersAPI = {
  getAll: (params) => api.get('/warehouse-transfers', { params }),
  getById: (id) => api.get(`/warehouse-transfers/${id}`),
  create: (data) => api.post('/warehouse-transfers', data),
  updateStatus: (id, status) => api.patch(`/warehouse-transfers/${id}/status`, { status }),
  cancel: (id) => api.post(`/warehouse-transfers/${id}/cancel`),
  statistics: () => api.get('/warehouse-transfers/statistics'),
};

// Roles API
export const rolesAPI = {
  getAll: (params) => api.get('/roles', { params }),
  getById: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
  listPermissions: () => api.get('/roles/permissions/list'),
};

// Analytics API
export const analyticsAPI = {
  overview: () => api.get('/analytics/overview'),
  sales: (params) => api.get('/analytics/sales', { params }),
  inventory: (params) => api.get('/analytics/inventory', { params }),
};

// Reports API
export const reportsAPI = {
  inventory: () => api.get('/reports/inventory'),
  sales: (params) => api.get('/reports/sales', { params }),
  lowStock: () => api.get('/reports/low-stock'),
};

// Notifications API
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.patch('/notifications/read-all'),
};

// ─── Health Check ────────────────────────────────────────────────────────────
/**
 * Ping the backend health endpoint
 * @returns {Promise<{ ok: boolean, latencyMs: number, status?: string }>}
 */
export const healthCheck = async () => {
  const start = performance.now();
  try {
    const res = await api.get('/health', { timeout: 5000 });
    return { ok: true, latencyMs: Math.round(performance.now() - start), status: res.data?.status };
  } catch {
    return { ok: false, latencyMs: Math.round(performance.now() - start) };
  }
};

/**
 * Deduplicated GET — coalesces identical in-flight GET requests
 * @param {string} url
 * @param {object} [config]
 */
export { deduplicatedGet };

export default api;
